import Itly from 'itly';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useWindowSize, DeviceSize } from 'hooks/useWindowSize';
import { ButtonWrapper, ExamplePrompt } from './styles';
import { useChatContext } from '../ChatContext';
import { useSampleQuestions } from '../useSampleQuestions';

export const ChatButton = ({ ariaLabel }: { ariaLabel: string }): JSX.Element => {
  const { isOpen, setIsOpen } = useChatContext();
  const sampleQuestions = useSampleQuestions();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // New state for the “cross‑fade” dance:
  const [displayText, setDisplayText] = useState(sampleQuestions[0]?.shortVersion || '');
  const [promptVisible, setPromptVisible] = useState(true);

  const { deviceSize } = useWindowSize();
  const isMobile = !deviceSize || deviceSize === DeviceSize.SMALL;
  const [isAboveStickyCTA, setIsAboveStickyCTA] = useState(false);

  const checkStickyCTA = useCallback(() => {
    const sticky = document.getElementById('sticky-cta-container');
    setIsAboveStickyCTA(!!sticky && sticky.classList.contains('show'));
  }, []);

  useEffect(
    function checkStickyCTAOnScroll() {
      window.addEventListener('scroll', checkStickyCTA, { passive: true });
      return () => window.removeEventListener('scroll', checkStickyCTA);
    },
    [checkStickyCTA]
  );

  useEffect(
    function checkyStickyCTAOnLoad() {
      setTimeout(() => checkStickyCTA(), 50);
    },
    [checkStickyCTA, isOpen]
  );

  useEffect(
    function setupSampleQuestionInterval() {
      if (!isOpen && sampleQuestions.length > 0 && !isMobile) {
        intervalRef.current = setInterval(() => {
          setCurrentQuestionIndex((prev) => (prev >= sampleQuestions.length - 1 ? 0 : prev + 1));
        }, 5000);
      }
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    },
    [isOpen, sampleQuestions.length, isMobile]
  );

  useEffect(
    function animateSamplePrompt() {
      if (sampleQuestions.length === 0 || isOpen || isMobile) {
        return;
      }

      setPromptVisible(false);

      const t = setTimeout(() => {
        setDisplayText(sampleQuestions[currentQuestionIndex].shortVersion);
        setPromptVisible(true);
      }, 400);

      return () => clearTimeout(t);
    },
    [currentQuestionIndex, sampleQuestions, isMobile, isOpen]
  );

  const isWide = useMemo(() => {
    return !isOpen && !isMobile;
  }, [isMobile, isOpen]);

  const handleClick = useCallback(() => {
    if (!isOpen) {
      Itly.productAssistantOpened({ current_url: window.location.href });
    }

    setIsOpen(!isOpen);
  }, [setIsOpen, isOpen]);

  return (
    <ButtonWrapper isAboveStickyCTA={isAboveStickyCTA} onClick={handleClick} aria-label={ariaLabel} isWide={isWide}>
      {isOpen && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18.7929 6.71782C19.1834 6.3273 19.1834 5.69413 18.7929 5.30361L18.6964 5.20711C18.3059 4.81658 17.6727 4.81658 17.2822 5.20711L12 10.4893L6.71782 5.20711C6.3273 4.81658 5.69413 4.81658 5.30361 5.20711L5.20711 5.30361C4.81658 5.69413 4.81658 6.3273 5.20711 6.71782L10.4893 12L5.20711 17.2822C4.81658 17.6727 4.81658 18.3059 5.20711 18.6964L5.30361 18.7929C5.69413 19.1834 6.3273 19.1834 6.71782 18.7929L12 13.5107L17.2822 18.7929C17.6727 19.1834 18.3059 19.1834 18.6964 18.7929L18.7929 18.6964C19.1834 18.3059 19.1834 17.6727 18.7929 17.2822L13.5107 12L18.7929 6.71782Z"
            fill="#333333"
          />
        </svg>
      )}
      {!isOpen && (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4.55375 20.5L3.5 19.4462L11.1308 11.8L6.75975 10.723L11.325 7.8885L10.929 2.5L15.048 5.977L20.025 3.95L18.023 8.94225L21.5 13.046L16.1115 12.675L13.2615 17.2402L12.175 12.8693L4.55375 20.5ZM5.14425 7.40375L3.5 5.7595L5.14425 4.1155L6.7885 5.7595L5.14425 7.40375ZM13.875 13.4348L15.3057 11.1038L18.0443 11.3077L16.275 9.20575L17.304 6.671L14.7693 7.7L12.6673 5.946L12.8712 8.66925L10.55 10.1155L13.2038 10.7712L13.875 13.4348ZM18.2405 20.5L16.5962 18.8557L18.2405 17.2115L19.8845 18.8557L18.2405 20.5Z"
            fill="#1F1F1F"
          />
        </svg>
      )}
      {!isMobile && !isOpen && (
        <div style={{ flexGrow: 1 }}>
          <ExamplePrompt
            className="example-prompt"
            style={{
              opacity: promptVisible ? 1 : 0,
              maxWidth: isWide && promptVisible ? '100%' : '0',
              margin: isWide && promptVisible ? '0 0 0 8px' : '0'
            }}
          >
            “{displayText}”
          </ExamplePrompt>
        </div>
      )}
    </ButtonWrapper>
  );
};
